'use client';

import type {
    CriteoAPIConfigurationSettings,
    SearchResult,
} from '../../../node_modules/@nxt-costco-com/forge-digital-components/dist/utils/criteo/types';
import { ReadonlyURLSearchParams } from 'next/navigation';
import { ProductTileConfigType } from 'node_modules/@nxt-costco-com/forge-digital-components/dist/components/Product/ProductTile/types';

import { ReactNode } from 'react';

import {
    GRS_REFINE_QUERY_PARAM,
    LW_REFINE_QUERY_PARAM,
} from '@/components/SearchResultsFacets/constants';
import { SEARCH_SERVICE_PROVIDERS } from '@/constants/grs';
import {
    fullyDecodeURI,
    getSelectedFacetsFromUrl,
} from '@/src/components/SearchResultsFacets/utils';
import { useSearchContext } from '@/src/services/search/SearchProvider';
import { GRSFacetConfigProperties } from '@/src/types/contentStack';
import type {
    ContentStackEntryDataProps,
    LocaleProps,
} from '@nxt-costco-com/forge-digital-components';
import { CriteoProviderClientComponent } from '@nxt-costco-com/forge-digital-components';

import {
    createCriteoSearchFiltersString,
    createCriteoSearchFiltersStringFromGRSFilters,
} from './utils';

export default function CriteoProviderWrapper({
    productTileConfigData,
    criteoAPIConfigurationSettings,
    lang,
    children,
    categoryId,
    isCategoryPage,
    grsFacetConfig,
    facetConfig,
    isMerchPage = false,
}: {
    productTileConfigData: ProductTileConfigType | undefined;
    criteoAPIConfigurationSettings: unknown | ContentStackEntryDataProps;
    lang: LocaleProps;
    children: ReactNode;
    categoryId?: string;
    isCategoryPage: boolean;
    grsFacetConfig:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined;
    facetConfig: ContentStackEntryDataProps | undefined;
    isMerchPage?: boolean;
}) {
    const {
        searchResult,
        searchedParams,
        isSearchResultsLoading,
        searchService,
    } = useSearchContext();

    const refinementKey =
        searchService === SEARCH_SERVICE_PROVIDERS.GRS
            ? GRS_REFINE_QUERY_PARAM
            : LW_REFINE_QUERY_PARAM;
    let refine = searchedParams?.[refinementKey as keyof typeof searchedParams];
    if (Array.isArray(refine)) {
        refine = refine[0];
    }

    const categoryHasSearchResults =
        isCategoryPage && categoryId && !isSearchResultsLoading && searchResult;

    let criteoRefinementString = '';
    if (facetConfig) {
        if (
            searchService === SEARCH_SERVICE_PROVIDERS.GRS &&
            searchResult &&
            grsFacetConfig
        ) {
            criteoRefinementString =
                createCriteoSearchFiltersStringFromGRSFilters(
                    getSelectedFacetsFromUrl({
                        searchParams: new URLSearchParams(
                            searchedParams as Record<string, string>,
                        ) as ReadonlyURLSearchParams,
                        searchResult: searchResult,
                        grsFacetConfig,
                        locale: lang,
                        searchService,
                        facetConfig,
                    }),
                    !isCategoryPage ? categoryId : undefined,
                );
        } else {
            criteoRefinementString = createCriteoSearchFiltersString(
                fullyDecodeURI(refine as string),
                !isCategoryPage ? categoryId : undefined,
            );
        }
    }
    return (
        <CriteoProviderClientComponent
            productTileConfigData={productTileConfigData!}
            criteoAPIConfigurationSettings={
                criteoAPIConfigurationSettings as CriteoAPIConfigurationSettings
            }
            locale={lang}
            categoryId={categoryId}
            searchResult={searchResult as SearchResult | undefined}
            isCategoryPage={isCategoryPage}
            searchParams={{
                ...(searchedParams ?? {}),
                refine: criteoRefinementString,
                sortBy: Array.isArray(searchedParams?.sortBy)
                    ? searchedParams.sortBy[0]
                    : searchedParams?.sortBy,
                currentPage: Array.isArray(searchedParams?.currentPage)
                    ? searchedParams.currentPage[0]
                    : searchedParams?.currentPage,
                page: 1,
            }}
            isSearchResultsLoading={isSearchResultsLoading}
            listSize={24}
            isTargetingDisabled={
                !isCategoryPage || categoryHasSearchResults
                    ? !searchResult?.isAdTargetingEnabled
                    : false
            }
            isMerchPage={isMerchPage}
        >
            {children}
        </CriteoProviderClientComponent>
    );
}
